'use client';

import {
  forwardRef,
  useRef,
  useImperativeHandle,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { type TurnstileInstance, Turnstile } from '@marsidev/react-turnstile';
import * as Sentry from '@sentry/nextjs';

import useTheme from '@edgevanta/ui/hooks/use-theme';
import { ShieldAlert } from '@edgevanta/ui/components/icon';
import toast from '@edgevanta/ui/lib/toast';

import env from '@/lib/env/client';

type TurnstileRef = {
  reset: () => void;
};

// Cloudflare client-side error codes:
// https://developers.cloudflare.com/turnstile/troubleshooting/client-side-errors/
// 2xxxxx = network unreachable, 3xxxxx = client execution (commonly ad blockers
// or privacy extensions), 6xxxxx = challenge solving (transient, just retry).
type ErrorKind =
  | 'network'
  | 'blocked'
  | 'unsupported'
  | 'transient'
  | 'generic';

const classifyErrorCode = (code: string): ErrorKind => {
  if (code.startsWith('2')) {
    return 'network';
  }
  if (code.startsWith('3')) {
    return 'blocked';
  }
  if (code.startsWith('6')) {
    return 'transient';
  }
  return 'generic';
};

const TurnstileWidget = forwardRef<
  TurnstileRef,
  {
    setToken: (token: string) => void;
  }
>(({ setToken }, ref) => {
  const { theme } = useTheme();
  const [errorKind, setErrorKind] = useState<ErrorKind | null>(null);

  const turnstileRef = useRef<TurnstileInstance>(null);

  useImperativeHandle(ref, () => ({
    reset: () => {
      setErrorKind(null);
      turnstileRef.current?.reset();
    },
  }));

  // Force a fresh challenge on mount. Guards against cached route segments
  // restoring a widget whose previous token has already been consumed.
  useEffect(() => {
    turnstileRef.current?.reset();
  }, []);

  const handleSuccess = useCallback(
    (token: string) => {
      setErrorKind(null);
      setToken(token);
    },
    [setToken],
  );

  const handleError = useCallback((errorCode?: string) => {
    const code = errorCode ?? 'unknown';
    const kind = classifyErrorCode(code);
    Sentry.captureException(new Error(`Turnstile widget error: ${code}`), {
      level: 'warning',
      tags: {
        'captcha-widget-error': 'true',
        'captcha-error-code': code,
        'captcha-error-kind': kind,
      },
    });
    // Transient (6xxxxx) — widget auto-retries the challenge. Skip UI noise.
    if (kind === 'transient') {
      return;
    }
    setErrorKind(kind);
    toast.error(
      kind === 'network'
        ? 'Could not reach Cloudflare to verify. Check your network connection and try again.'
        : kind === 'blocked'
          ? 'Security verification was blocked. An ad blocker or privacy extension is likely interfering — disable it for this page and refresh.'
          : 'Security verification failed. Please refresh the page, or try a different browser or an incognito window with extensions turned off.',
      { duration: 8000 },
    );
  }, []);

  const handleUnsupported = useCallback(() => {
    Sentry.captureException(new Error('Turnstile: unsupported browser'), {
      level: 'warning',
      tags: { 'captcha-unsupported-browser': 'true' },
    });
    setErrorKind('unsupported');
    toast.error(
      'Your browser is not supported by our security verification. Please update or try a different browser.',
      { duration: 10000 },
    );
  }, []);

  const handleTimeout = useCallback(() => {
    setToken('');
    turnstileRef.current?.reset();
    toast.warning('Security verification timed out. Verifying again…', {
      duration: 3000,
    });
  }, [setToken]);

  const handleScriptError = useCallback(() => {
    Sentry.captureException(new Error('Turnstile script failed to load'), {
      level: 'warning',
      tags: { 'captcha-script-error': 'true' },
    });
    setErrorKind('blocked');
    toast.error(
      'Security verification could not be loaded. This is often caused by ad blockers or privacy extensions. Disable them for this page and refresh, or try a different browser.',
      { duration: 10000 },
    );
  }, []);

  const handleExpire = useCallback(() => {
    setToken('');
    turnstileRef.current?.reset();
    toast.warning('Security verification expired. Verifying again…', {
      duration: 3000,
    });
  }, [setToken]);

  if (!env.NEXT_PUBLIC_TURNSTILE_SITE_KEY) {
    return null;
  }

  return (
    <div className="space-y-2">
      <div className={errorKind ? 'hidden' : undefined}>
        <Turnstile
          onError={handleError}
          onExpire={handleExpire}
          onSuccess={handleSuccess}
          onTimeout={handleTimeout}
          onUnsupported={handleUnsupported}
          options={{
            theme: theme === 'dark' ? 'dark' : 'light',
            size: 'flexible',
          }}
          ref={turnstileRef}
          scriptOptions={{ onError: handleScriptError }}
          siteKey={env.NEXT_PUBLIC_TURNSTILE_SITE_KEY}
        />
      </div>

      {errorKind ? (
        <div
          aria-live="polite"
          className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm"
          role="alert"
        >
          <div className="flex items-center gap-2 text-foreground">
            <ShieldAlert
              aria-hidden="true"
              className="size-4 shrink-0 text-destructive"
            />
            <p className="font-medium">
              {errorKind === 'unsupported'
                ? 'Browser not supported'
                : errorKind === 'network'
                  ? "Couldn't reach security verification"
                  : errorKind === 'blocked'
                    ? 'Security verification was blocked'
                    : 'Security verification failed'}
            </p>
          </div>
          <div className="mt-2 text-muted-foreground">
            {errorKind === 'unsupported' ? (
              <p>
                Update your browser or try a different one (Chrome, Firefox,
                Edge, or Safari).
              </p>
            ) : errorKind === 'network' ? (
              <p>Check your internet connection and refresh the page.</p>
            ) : errorKind === 'blocked' ? (
              <ul className="ml-4 list-disc space-y-1">
                <li>
                  Disable ad blockers or privacy extensions (e.g. uBlock Origin,
                  AdBlock, Brave Shields) for this page.
                </li>
                <li>Refresh the page after disabling.</li>
                <li>
                  If the issue persists, try a different browser or an incognito
                  window with extensions turned off.
                </li>
              </ul>
            ) : (
              <ul className="ml-4 list-disc space-y-1">
                <li>Refresh the page and try again.</li>
                <li>
                  If the issue persists, try a different browser or an incognito
                  window with extensions turned off.
                </li>
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
});

TurnstileWidget.displayName = 'TurnstileWidget';

export { TurnstileWidget as default, type TurnstileRef };
